<template>
  <div class="symbology-swatches">
    <div v-for="(item, idx) in swatches" :key="idx" class="legend-entry">
      <img
        v-if="item.symbol && item.symbol.type === 'esriPMS'"
        class="legend-icon"
        :src="iconSrc(item.symbol)"
        :alt="item.label"
      >
      <span v-else class="legend-symbol">
        <svg width="20" height="20" viewBox="0 0 20 20">
          <rect
            v-if="isPolygon(item.symbol)"
            x="2" y="2" width="16" height="16"
            :fill="fillColor(item.symbol)"
            :stroke="strokeColor(item.symbol)"
            :stroke-width="lineWidth(item.symbol.outline)"
            :stroke-dasharray="lineDashArray(item.symbol.outline)"
          />
          <line
            v-else-if="isLine(item.symbol)"
            x1="1" y1="10" x2="19" y2="10"
            :stroke="fillColor(item.symbol)"
            :stroke-width="lineWidth(item.symbol)"
            :stroke-dasharray="lineDashArray(item.symbol)"
          />
          <circle
            v-else-if="shapeKind(item.symbol) === 'circle'"
            cx="10" cy="10" :r="radius(item.symbol)"
            :fill="fillColor(item.symbol)" :stroke="strokeColor(item.symbol)" :stroke-width="strokeWidth(item.symbol)"
          />
          <rect
            v-else-if="shapeKind(item.symbol) === 'square'"
            :x="10 - radius(item.symbol)" :y="10 - radius(item.symbol)"
            :width="radius(item.symbol) * 2" :height="radius(item.symbol) * 2"
            :fill="fillColor(item.symbol)" :stroke="strokeColor(item.symbol)" :stroke-width="strokeWidth(item.symbol)"
          />
          <rect
            v-else-if="shapeKind(item.symbol) === 'diamond'"
            :x="10 - radius(item.symbol)" :y="10 - radius(item.symbol)"
            :width="radius(item.symbol) * 2" :height="radius(item.symbol) * 2"
            transform="rotate(45 10 10)"
            :fill="fillColor(item.symbol)" :stroke="strokeColor(item.symbol)" :stroke-width="strokeWidth(item.symbol)"
          />
          <polygon
            v-else-if="shapeKind(item.symbol) === 'triangle'"
            :points="trianglePoints(item.symbol)"
            :fill="fillColor(item.symbol)" :stroke="strokeColor(item.symbol)" :stroke-width="strokeWidth(item.symbol)"
          />
          <polygon
            v-else-if="shapeKind(item.symbol) === 'star'"
            :points="starPoints(item.symbol)"
            :fill="fillColor(item.symbol)" :stroke="strokeColor(item.symbol)" :stroke-width="strokeWidth(item.symbol)"
          />
          <g v-else-if="shapeKind(item.symbol) === 'cross'">
            <line :x1="10 - radius(item.symbol)" y1="10" :x2="10 + radius(item.symbol)" y2="10" :stroke="fillColor(item.symbol)" :stroke-width="armWidth(item.symbol)" />
            <line x1="10" :y1="10 - radius(item.symbol)" x2="10" :y2="10 + radius(item.symbol)" :stroke="fillColor(item.symbol)" :stroke-width="armWidth(item.symbol)" />
          </g>
          <g v-else-if="shapeKind(item.symbol) === 'x'">
            <line :x1="10 - radius(item.symbol)" :y1="10 - radius(item.symbol)" :x2="10 + radius(item.symbol)" :y2="10 + radius(item.symbol)" :stroke="fillColor(item.symbol)" :stroke-width="armWidth(item.symbol)" />
            <line :x1="10 - radius(item.symbol)" :y1="10 + radius(item.symbol)" :x2="10 + radius(item.symbol)" :y2="10 - radius(item.symbol)" :stroke="fillColor(item.symbol)" :stroke-width="armWidth(item.symbol)" />
          </g>
        </svg>
      </span>
      <span class="legend-label">{{ item.label }}</span>
    </div>
  </div>
</template>

<script>
import { esriColor, rendererSwatches, lineDashFor, PT_TO_PX } from "@/services/layers/symbology";

const SHAPE_KINDS = {
  esriSMSSquare: "square",
  esriSMSDiamond: "diamond",
  esriSMSTriangle: "triangle",
  esriSMSCross: "cross",
  esriSMSX: "x",
  terrainSMSStar: "star",
};

export default {
  name: "SymbologySwatches",
  props: {
    renderer: { type: Object, default: null },
  },
  computed: {
    swatches() {
      return rendererSwatches(this.renderer);
    },
  },
  methods: {
    isLine(symbol) {
      return !!symbol && symbol.type === "esriSLS";
    },
    isPolygon(symbol) {
      return !!symbol && symbol.type === "esriSFS";
    },
    // Mismo criterio cosmético que radius(): el ancho real puede desbordar el
    // viewBox fijo 20x20.
    lineWidth(symbol) {
      return Math.min(9, ((symbol && symbol.width) || 1) * PT_TO_PX);
    },
    lineDashArray(symbol) {
      const dash = lineDashFor(symbol && symbol.style, this.lineWidth(symbol));
      return dash ? dash.join(" ") : null;
    },
    shapeKind(symbol) {
      return (symbol && SHAPE_KINDS[symbol.style]) || "circle";
    },
    // Radio cosmético del swatch (preview): no reproduce a escala exacta el
    // símbolo del mapa, sólo lo aproxima dentro del viewBox fijo 20x20.
    radius(symbol) {
      const size = (symbol && symbol.size) || 8;
      return Math.min(9, size / 2);
    },
    armWidth(symbol) {
      return Math.max(2, this.radius(symbol) / 2.5);
    },
    fillColor(symbol) {
      return esriColor(symbol && symbol.color);
    },
    strokeColor(symbol) {
      return symbol && symbol.outline ? esriColor(symbol.outline.color) : "none";
    },
    strokeWidth(symbol) {
      return symbol && symbol.outline ? Math.max(0.5, (symbol.outline.width || 0) / 2) : 0;
    },
    trianglePoints(symbol) {
      const r = this.radius(symbol);
      const points = [-90, 30, 150].map(deg => {
        const rad = (deg * Math.PI) / 180;
        return `${10 + r * Math.cos(rad)},${10 + r * Math.sin(rad)}`;
      });
      return points.join(" ");
    },
    starPoints(symbol) {
      const outer = this.radius(symbol);
      const inner = outer * 0.45;
      return Array.from({ length: 10 }, (_, index) => {
        const radius = index % 2 === 0 ? outer : inner;
        const rad = ((-90 + index * 36) * Math.PI) / 180;
        return `${10 + radius * Math.cos(rad)},${10 + radius * Math.sin(rad)}`;
      }).join(" ");
    },
    iconSrc(symbol) {
      return symbol.imageData
        ? `data:${symbol.contentType || "image/png"};base64,${symbol.imageData}`
        : symbol.url;
    },
  },
};
</script>
